<template>
  <div>
    <draggable v-if="contents && contents.length" v-model="orderedContents" tag="ul" handle=".ccl-drag-handle" :disabled="!isOwner || orderSaving"
               class="course-curriculum-list" @change="saveOrder">
      <li v-for="content in orderedContents" :key="getContentKey(content)"
          :class="[getContentClass(content), {
            'ccl-item--owner': isOwner,
            'ccl-item--external': isExternalContent(content)
          }]"
          @click="handleClick(content)">
        <button v-if="isOwner" class="ccl-drag-handle" type="button"
                :aria-label="$t('general.sort_list')" :title="$t('general.sort_list')" @click.stop>
          <span class="ccl-drag-grip" aria-hidden="true"></span>
        </button>

        <div class="ccl-content-main">
          <div v-if="isExternalContent(content)" class="icon-cat d-flex align-items-center">
            <i :class="content.file_category.icon" class="mr-1"></i>
            <span class="ccl-category-label">{{ content.file_category.name }}</span>
          </div>
          <span class="ccl-content-title">{{ content.title }}</span>
        </div>

        <span v-if="isOwner" class="ccl-actions" @click.stop>
          <button v-if="canEditContent(content)" class="btn ccl-action-btn ccl-edit-btn" type="button"
                  :aria-label="$t('general.edit')" @click="$emit('edit', content)">
            <i class="uil-pen" aria-hidden="true"></i>
          </button>
          <button class="btn ccl-action-btn ccl-remove-btn" type="button"
                  :aria-label="$t('general.remove')" @click="$emit('remove', content.pivot.id)">
            <i class="uil-trash" aria-hidden="true"></i>
          </button>
        </span>
      </li>
    </draggable>
    <div v-if="isOwner && orderSaving" class="ccl-saving-row">
      <i class="uil-spinner-alt"></i> {{ $t('general.saving') }}
    </div>
    <div v-if="!contents || !contents.length" class="ccl-empty">
      <i class="uil-folder-open ccl-empty__icon"></i>
      <p class="ccl-empty__text">{{ $t('general.no_added_content') }}</p>
    </div>
  </div>
</template>

<script>
import draggable from "vuedraggable";

export default {
  name: "CollectionContentList",
  components: { draggable },
  props: {
    contents: {
      type: Array,
      default: () => []
    },
    isOwner: {
      type: Boolean,
      default: false
    },
    orderSaving: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      orderedContents: []
    };
  },
  watch: {
    contents: {
      handler(val) {
        this.orderedContents = Array.isArray(val) ? [...val] : [];
      },
      immediate: true
    }
  },
  methods: {
    getContentKey(content) {
      return content && content.pivot && content.pivot.id ? content.pivot.id : content.id;
    },
    getContentClass(content) {
      const type = content.pivot ? this.checkModelAble(content.pivot.contentable_type) : null;
      if (type === 'CollectionExternalContent') return 'read';
      if (type === 'Assignment') return 'interactive';
      const typeId = content.training_content_type_id;
      return { read: typeId === 1, watch: typeId === 2, quiz: typeId === 3, interactive: typeId === 4 };
    },
    getModelRouteName(model = '') {
      const modelName = model.split('\\').pop();
      switch (modelName) {
        case 'Assignment': return 'user.assignment-detail';
        case 'Training': return 'user.education-detail';
        default: return '';
      }
    },
    checkModelAble(model = '') {
      return model.split('\\').pop();
    },
    isExternalContent(content) {
      return !content.pivot || this.checkModelAble(content.pivot.contentable_type) === 'CollectionExternalContent' || content.pivot.contentable_type == null;
    },
    canEditContent(content) {
      return content && this.isExternalContent(content) && content.pivot && content.pivot.id;
    },
    saveOrder() {
      this.$emit('save-order', this.orderedContents);
    },
    handleClick(content) {
      if (this.isExternalContent(content)) {
        this.$emit('download', content.url || content.file_link);
      } else {
        this.$router.push({
          name: this.getModelRouteName(content.pivot.contentable_type),
          params: { id: content.pivot.contentable_id }
        });
      }
    }
  }
};
</script>

<style scoped>
/* ═══════════════════════════════════════
   GENEL (Web + Mobil)
   ═══════════════════════════════════════ */

/* Başlık — parent .cd-content-card padding'i ile uyumlu */
.ccl-title {
  font-size: 17px;
  font-weight: 600;
  color: #222;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  margin: 0;
  border-bottom: 1px solid #eef0f3;
}

.ccl-title i {
  color: var(--backgroundColor, #666CFF);
  font-size: 20px;
}

/* Boş durum */
.ccl-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  text-align: center;
}

.ccl-empty__icon {
  font-size: 48px;
  color: #ccc;
  margin-bottom: 12px;
}

.ccl-empty__text {
  font-size: 15px;
  color: #999;
  margin: 0;
}

/* Liste container: border-radius 14px, soft shadow */
.course-curriculum-list {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  list-style: none;
  padding: 0;
  margin: 0;
}

/* Liste item: min-height, border-bottom, spacing */
.course-curriculum-list li {
  min-height: 56px;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 8px;
  padding: 12px 16px;
  font-size: 16px;
  line-height: 1.5;
  color: #333;
  cursor: pointer;
  overflow: hidden;
  white-space: normal;
  border-bottom: 1px solid #eef0f3;
  -webkit-tap-highlight-color: transparent;
}

.course-curriculum-list li:last-child {
  border-bottom: none;
}

/* Active state — tıklama feedback */
.course-curriculum-list li:active {
  background: #f5f6fa;
}

.course-curriculum-list li.sortable-ghost {
  opacity: 0.45;
  background: #f8fafc;
  border: 1px dashed #94a3b8;
}

.course-curriculum-list li.sortable-chosen {
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
}

.course-curriculum-list li.sortable-drag {
  cursor: grabbing;
}

.ccl-content-main {
  min-width: 0;
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ccl-content-title {
  min-width: 0;
  display: block;
  overflow: hidden;
  color: #27364b;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Kategori ikon + text */
.course-curriculum-list li .icon-cat {
  flex-shrink: 0;
  font-size: 14px;
  color: #475569;
  background: #f1f5f9;
  gap: 4px;
}

.ccl-category-label {
  min-width: 0;
}

.icon-cat i {
  font-size: 16px;
}

.ccl-drag-handle {
  width: 32px;
  height: 32px;
  border: 0;
  background: transparent;
  color: #9aa0aa;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: grab;
  padding: 0;
  touch-action: none;
  user-select: none;
  transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.ccl-drag-handle:active {
  cursor: grabbing;
  transform: scale(0.94);
}

.ccl-drag-handle:hover {
  color: #344054;
  background: #f1f5f9;
}

.ccl-drag-grip {
  display: block;
  width: 12px;
  height: 18px;
  color: inherit;
  background-image: radial-gradient(circle, currentColor 1.35px, transparent 1.55px);
  background-position: center;
  background-size: 6px 6px;
  opacity: 0.9;
}

.ccl-drag-handle:focus-visible,
.course-curriculum-list .ccl-action-btn:focus-visible {
  outline: 2px solid #64748b;
  outline-offset: 2px;
}

/* Sil butonu: touch target 44x44, active state */
.ccl-actions {
  position: static;
  right: auto;
  margin-left: auto;
  display: inline-flex;
  gap: 8px;
  flex-shrink: 0;
}

.course-curriculum-list .ccl-action-btn {
  min-width: 44px;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  flex-shrink: 0;
}

.course-curriculum-list .ccl-edit-btn {
  border: 1px solid #d7dee8;
  background: #f8fafc;
  color: #344054;
  box-shadow: none;
}

.course-curriculum-list .ccl-edit-btn i {
  color: inherit;
}

.course-curriculum-list .ccl-remove-btn {
  border: 1px solid #d7dee8;
  background: #fff;
  color: #b42318;
  box-shadow: none;
}

.course-curriculum-list .ccl-remove-btn i {
  color: inherit;
}

.course-curriculum-list .ccl-action-btn:active {
  transform: scale(0.98);
  opacity: 0.85;
}

.ccl-saving-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background: #fff;
  border-top: 1px solid #eef0f3;
  color: #667085;
  font-size: 14px;
  font-weight: 600;
}

/* ═══════════════════════════════════════
   MOBİL (≤768px)
   ═══════════════════════════════════════ */
@media (max-width: 768px) {
  .course-curriculum-list {
    display: grid;
    gap: 10px;
    overflow: visible;
    background: transparent;
    border-radius: 0;
    box-shadow: none;
  }

  /* Hover efekti devre dışı */
  .course-curriculum-list .ccl-action-btn:hover {
    transform: none !important;
  }

  /* Her içerik mobilde okunabilir bağımsız bir karttır. */
  .course-curriculum-list li {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas: "title";
    align-items: start;
    padding: 13px;
    column-gap: 10px;
    row-gap: 12px;
    overflow: visible;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
    white-space: normal;
  }

  .course-curriculum-list li.ccl-item--external:not(.ccl-item--owner) {
    grid-template-areas:
      "title"
      "category";
  }

  .course-curriculum-list li.ccl-item--owner {
    grid-template-columns: 34px minmax(0, 1fr) auto;
    grid-template-areas:
      "handle title title"
      "category category actions";
  }

  .ccl-drag-handle {
    grid-area: handle;
    width: 34px;
    height: 34px;
    color: #64748b;
    background: #f8fafc;
    border: 1px solid #d7dee8;
    border-radius: 9px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
  }

  .ccl-content-main {
    display: contents;
  }

  .ccl-content-title {
    grid-area: title;
    align-self: center;
    width: 100%;
    overflow: visible;
    font-size: 15px;
    line-height: 1.4;
    overflow-wrap: anywhere;
    text-overflow: clip;
    white-space: normal;
  }

  .icon-cat {
    grid-area: category;
    align-self: center;
    width: fit-content;
    max-width: 100%;
    min-width: 0 !important;
    margin-right: 0 !important;
    padding: 4px 8px !important;
    color: #475569 !important;
    background: #f1f5f9 !important;
    border: 1px solid #dbe3ec;
    border-radius: 7px !important;
    font-size: 11px !important;
    line-height: 1.25;
    white-space: nowrap;
    overflow: hidden;
  }

  .ccl-category-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ccl-actions {
    grid-area: actions;
    align-self: end;
    justify-self: end;
    width: auto;
    justify-content: flex-end;
    margin-left: 0;
    gap: 6px;
  }

  .course-curriculum-list .ccl-action-btn {
    width: 34px;
    min-width: 34px;
    min-height: 34px;
    padding: 0;
  }

  /* Sil butonu: belirgin, ikon büyütme */
  .course-curriculum-list .ccl-action-btn i {
    font-size: 18px;
  }
}
</style>
